import { API, Auth } from 'aws-amplify';
import React, { FC, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useParams } from 'react-router';
import { RootState, useDispatch, useSelector } from 'src/store';
import {
  editTextReset,
  editColor,
  editFontSize,
  editFontWeight,
  editPreFontSize,
  editText
} from 'src/store/modules/editText';
import {
  buttonsReset,
  editBtnBgColor,
  editBtnColor,
  editBtnFontSize,
  editBtnFontWeight,
  editBtnPreFontSize,
  editBtnText
} from 'src/store/modules/buttons';
import {
  imageReset,
  editBgImage,
  editRadius,
  editCurrentIndex,
  editDeeplink
} from 'src/store/modules/imageUpload';

import setInitValue from 'src/utils/setInitValue';
import { changeText } from 'src/utils/changeText';
import { changeEnter } from 'src/utils/changeEnter';
import { editUserCompany } from 'src/store/modules/client';
import { trackingReset } from 'src/store/modules/trackings';
import { getFontURL } from 'src/utils/getFontUrl';
import { initTrackingValues } from '../TemplateTracking/BottomSheetSlideTracking';

const initViewValues = {
  currentIndex: 0,
  bgImage: {
    multi_image: [
      'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp',
      'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp',
      'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp'
    ]
  },
  bgOpacity: { bgDimmed: 70 },
  radius: { multi_image: 16, multi_button: 8 },
  deeplink: {
    multi_image: [
      'https://www.inappmessage.com/',
      'https://www.inappmessage.com/',
      'https://www.inappmessage.com/'
    ]
  },

  text: {
    multi_title: ['SALE UP TO 30%', 'SALE UP TO 40%', 'SALE UP TO 50%'],
    multi_sub_title: [
      'Get a designer brand products at a sale price.',
      'Get a designer brand.',
      'Give away a 10$ coupon for members.'
    ]
  },
  fontSize: { multi_title: 1.5, multi_sub_title: 1 },
  fontWeight: { multi_title: 700, multi_sub_title: 400 },
  color: { multi_title: '#000000', multi_sub_title: '#000000' },
  fontFamily: { multi_title: 'Montserrat', multi_sub_title: 'Montserrat' },
  textAlign: { multi_title: 'left', multi_sub_title: 'left' },

  btnText: {
    multi_left_button: ['Close 1', 'Close 2', 'Close 3'],
    multi_right_button: ['See more 1', 'See more 2', 'See more 3']
  },
  btnFontSize: {
    multi_left_button: 0.75,
    multi_right_button: 0.75
  },
  btnFontWeight: {
    multi_left_button: 500,
    multi_right_button: 500
  },
  btnColor: {
    multi_left_button: '#828282',
    multi_right_button: '#000000'
  },
  btnBgColorR: { multi_left_button: 0, multi_right_button: 0 },
  btnBgColorG: { multi_left_button: 0, multi_right_button: 0 },
  btnBgColorB: { multi_left_button: 0, multi_right_button: 0 },
  btnBgColorA: { multi_left_button: 0, multi_right_button: 0 },
  btnFontFamily: {
    multi_left_button: 'Montserrat',
    multi_right_button: 'Montserrat'
  }
};

const BottomSheetSlide: FC = () => {
  const dispatch = useDispatch();
  const sliderRef = useRef<any>(null);
  const contentsSliderRef = useRef<any>(null);

  useEffect(() => {
    async function getUserId() {
      try {
        const current = await Auth.currentUserInfo();
        dispatch(editUserCompany(current.attributes['custom:Company']));
      } catch (error) {
        console.log('error');
      }
    }
    getUserId();
  }, []);

  const clientName = useSelector(
    (state: RootState) => state.client.userCompany
  );
  const templateWidth = useSelector(
    (state: RootState) => state.template.templateWidth
  );
  const currentIndex = useSelector(
    (state: RootState) => state.imageUpload.currentIndex
  );
  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);

  const radius = useSelector((state: RootState) => state.imageUpload.radius);

  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);

  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.7;

  const multiTitleURL = getFontURL(fontFamily?.multi_title);
  const multiSubTitleURL = getFontURL(fontFamily?.multi_sub_title);
  const multiLeftButtonURL = getFontURL(btnFontFamily?.multi_left_button);
  const multiRightButtonURL = getFontURL(btnFontFamily?.multi_right_button);

  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    async function onLoad() {
      try {
        if (id) {
          const { customValues } = await API.get(
            'iam-editor',
            `/templates/${id}`,
            ''
          );
          if (
            customValues?.bgImage &&
            customValues?.text &&
            customValues?.btnText
          ) {
            setInitValue(dispatch, customValues);
          } else {
            setValue(
              dispatch,
              customValues,
              { ...initViewValues, ...initTrackingValues },
              clientName
            );
          }
        }
      } catch (error) {}
    }
    setInitValue(dispatch, { ...initViewValues, ...initTrackingValues });
    onLoad();
    return () => {
      dispatch(buttonsReset());
      dispatch(editTextReset());
      dispatch(imageReset());
      dispatch(trackingReset());
    };
  }, []);

  const style = `
  @font-face {
    font-family: 'multiTitle';
    src: url(${multiTitleURL});
    }

  @font-face {
    font-family: 'multiSubTitle';
    src: url(${multiSubTitleURL});
    }
  
  @font-face {
    font-family: 'multiLeftButton';
    src: url(${multiLeftButtonURL});
    }


  @font-face {
    font-family: 'multiRightButton';
    src: url(${multiRightButtonURL});
    }
  .root_view {
    width: 100%;
    height: 100%;
    
    position: relative;
    background: rgba(0, 0, 0, ${displayValue});
    overflow: hidden;
    
    .modal_wrapper {
      width: 100%;
      height: 20.5rem;

      position: absolute;
      bottom: 0;

      overflow: hidden;
      border-radius: ${radius?.multi_image}px ${radius?.multi_image}px 0 0;
      background-color: transparent;
    }

    .slick_wrapper,
    .slick_wrapper .slick-slider,
    .slick_wrapper .slick-list,
    .slick_wrapper .slick-track div,
    .slick_wrapper .slick-track img {
      width: 100%;
      height: 16rem;
      outline: none;
    }
    .slick-track {
      display: flex;
    }
    .slick-track img {
      object-fit: cover;
    }
    .slick_page {
      padding: 0.25rem 1rem;
      position: absolute;
      top: 1rem;
      right: 1rem;

      display: flex;
      gap: 0.25rem;

      color: #fff;
      font-family: Roboto;
      font-size: 0.875rem;
      font-weight: 500;

      border-radius: 999px;
      background-color: rgba(0, 0, 0, 0.8);
      z-index: 1;
    }

    .contents_wrapper {
      width: 100%;
      margin-top: 1.5rem;
      padding: 0 7%;

      position: absolute;
      top: 0;
      z-index: 1;
    }
    .contents_box .title {
      width: 100%;
      min-height: 48px;
      margin-bottom: 12px;

      display: flex;
      align-items: center;
    }
    .contents_box .title .title_inner {
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      text-align: ${textAlign?.multi_title};
      color: ${color?.multi_title};
      font-size: ${fontSize?.multi_title}rem;
      font-weight: ${fontWeight?.multi_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.multi_title
        )
          ? fontFamily?.multi_title
          : 'multiTitle'
      };
    }
    .contents_box .sub_title {
      width: 100%;
      height: 100%;

      display: flex;
      align-items: center;
    }
    .contents_box .sub_title_inner {
      width: 100%;
      max-height: 50px;
      
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;

      text-align: ${textAlign?.multi_sub_title};
      color: ${color?.multi_sub_title};
      font-size: ${fontSize?.multi_sub_title}rem;
      font-weight: ${fontWeight?.multi_sub_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.multi_sub_title
        )
          ? fontFamily?.multi_sub_title
          : 'multiSubTitle'
      };
    }

    .slick-slider {
      position: relative;
    }
    .dot_wrapper {
      width: 100%;
      position: absolute;
      bottom: 1rem;
      z-index: 999;
    }
    .dot_wrapper,
    .dot_wrapper li {
      height: 0.25rem;      
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      list-style-type: none;
    }
    .dot_wrapper button {
      width: 0.25rem;
      height: 0.25rem;

      font-size: 0;
      border: 0;
      outline: 0;
      border-radius: 999px;
      background-color: rgba(0, 0, 0, .3);
    }
    .dot_wrapper li.slick-active button {
      background-color: #000;
    }

    .btn_wrapper {
      width: 100%;
      height: 4.5rem;
      padding: 1rem 10%;
      padding-bottom: 1.5rem;

      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      background-color: #fff;
    }
    .btn_wrapper button {
      border: 0;
    }
    .btn_wrapper .style01 {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      text-align: left;
      font-size: ${btnFontSize?.multi_left_button}rem;
      font-weight: ${btnFontWeight?.multi_left_button};
      color: ${btnColor?.multi_left_button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.multi_left_button
        )
          ? btnFontFamily?.multi_left_button
          : 'multiLeftButton'
      };
      background-color:rgba(${btnBgColorR?.multi_left_button},${
    btnBgColorG?.multi_left_button
  }, ${btnBgColorB?.multi_left_button}, ${btnBgColorA?.multi_left_button});
    }
    .btn_wrapper .style02 {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      text-align: right;
      font-size: ${btnFontSize?.multi_right_button}rem;
      font-weight: ${btnFontWeight?.multi_right_button};
      color: ${btnColor?.multi_right_button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.multi_right_button
        )
          ? btnFontFamily?.multi_right_button
          : 'multiRightButton'
      };
      background-color:rgba(${btnBgColorR?.multi_right_button},${
    btnBgColorG?.multi_right_button
  }, ${btnBgColorB?.multi_right_button}, ${btnBgColorA?.multi_right_button});
    }

    ${
      templateWidth > 767
        ? `
          .modal_wrapper {
            height: 36.5rem;
          }
          .slick_wrapper,
          .slick_wrapper .slick-slider,
          .slick_wrapper .slick-list,
          .slick_wrapper .slick-track div,
          .slick_wrapper .slick-track img {
            height: 30rem;
          }
          .slick_page {
            top: 2.5rem;
            right: 2.5rem;
          }

          .contents_wrapper {
            margin-top: 0;
            padding: 3rem;
          }
          .contents_box .title {
            font-size: ${fontSize?.multi_title + 0.75}rem;
          }
          .contents_box .sub_title {
            height: 3rem;
            font-size: ${fontSize?.multi_sub_title + 0.5}rem;
          }
          .contents_box .sub_title_inner {
            max-height: 3rem;
          }

          .btn_wrapper {
            height: 6.5rem;
            padding: 2rem;
            padding-top: 1.5rem;
          }
          .style01 {
            font-size: ${btnFontSize?.multi_left_button + 0.5}rem;
          }
          .style02 {
            font-size: ${btnFontSize?.multi_right_button + 0.5}rem;
          }
        `
        : ''
    }
  }
  `;

  const settings = {
    arrows: false,
    infinite: true,
    dots: true,

    dotsClass: 'dot_wrapper',
    appendDots: dots => <div id="id_dot_wrapper">{dots}</div>,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 300,
    touchThreshold: 300
  };
  // appendDots : $('.dot_wrapper'),

  const contentsSettings = {
    arrows: false,
    infinite: true,
    fade: true,
    swipe: false,

    speed: 300,
    touchThreshold: 300
  };

  useEffect(() => {
    sliderRef.current.slickGoTo(currentIndex);
    contentsSliderRef.current.slickGoTo(currentIndex);
  }, [currentIndex]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div className="root_view">
        <div className="modal_wrapper">
          <div className="slick_wrapper">
            <Slider
              {...settings}
              ref={sliderRef}
              afterChange={index => {
                dispatch(editCurrentIndex(index));
              }}
            >
              {bgImage?.multi_image?.map((data: any, idx: number) => (
                <div key={idx}>
                  <img
                    className="image"
                    src={bgImage?.multi_image[idx]}
                    alt="half top"
                  />
                </div>
              ))}
            </Slider>
          </div>
          <div className="contents_wrapper">
            <Slider {...contentsSettings} ref={contentsSliderRef}>
              {text?.multi_title?.map((data: any, idx: number) => (
                <div key={idx} className="contents_box">
                  <div className="title">
                    <div className="title_inner">
                      {changeEnter(text?.multi_title?.[idx])}
                    </div>
                  </div>
                  <div className="sub_title">
                    <div className="sub_title_inner">
                      {changeEnter(text?.multi_sub_title?.[idx])}
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          <div className="btn_wrapper">
            <button className="style01">
              {btnText?.multi_left_button?.[currentIndex]}
            </button>
            <button className="style02">
              {btnText?.multi_right_button?.[currentIndex]}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const setValue = (
  dispatch: any,
  values: any,
  initViewValuess: any,
  clientName: any
) => {
  // console.log(values);
  // console.log('test');
  // dispatch(editBgColor(values?.bgColor));
  const TYPE = typeof values?.imageName;
  if (TYPE === 'string') {
    dispatch(
      editBgImage({
        multi_image: [
          `https://image.inappmessage.com/${clientName}/${values?.imageName}`
        ]
      })
    );
    // dispatch(editBgImageBlur(values?.bgImageBlur));
    dispatch(editDeeplink({ multi_image: [values?.newDeepLink] }));
    dispatch(
      editRadius({
        ...initViewValuess?.radius,
        ...{ multi_image: values?.newImgRadius }
      })
    );
    dispatch(
      editText({
        multi_title: [changeText(values?.newTitle)],
        multi_sub_title: [changeText(values?.newSubTitle)]
      })
    );

    dispatch(
      editBtnText({
        multi_button: [changeText(values?.mainBtnText)],
        multi_left_button: [changeText(values?.leftBtnText)],
        multi_right_button: [changeText(values?.rightBtnText)]
      })
    );
  } else {
    const MultiBackground = values?.imageName?.map(
      (data: any) => `https://image.inappmessage.com/${clientName}/${data}`
    );
    dispatch(
      editBgImage({
        multi_image: MultiBackground
      })
    );
    // dispatch(editBgImageBlur(values?.bgImageBlur));
    dispatch(editDeeplink({ multi_image: values?.newDeepLink }));
    dispatch(
      editRadius({
        ...initViewValuess?.radius,
        ...{ multi_image: values?.newImgRadius }
      })
    );

    dispatch(
      editText({
        multi_title: values?.newTitle,
        multi_sub_title: values?.newSubTitle
      })
    );

    dispatch(
      editBtnText({
        multi_button: values?.mainBtnText,
        multi_left_button: values?.leftBtnText,
        multi_right_button: values?.rightBtnText
      })
    );
  }

  dispatch(
    editPreFontSize({
      multi_title: values?.newTitleSize / 16,
      multi_sub_title: values?.newSubTitleSize / 16
    })
  );
  dispatch(
    editFontSize({
      multi_title: values?.newTitleSize / 16,
      multi_sub_title: values?.newSubTitleSize / 16
    })
  );
  dispatch(
    editFontWeight({
      multi_title: values?.newTitleWeight,
      multi_sub_title: values?.newSubTitleWeight
    })
  );
  dispatch(
    editColor({
      multi_title: values?.newTitleColor,
      multi_sub_title: values?.newSubTitleColor
    })
  );
  // dispatch(editFontFamily(values?.fontFamily));
  // dispatch(editTextAlign(values?.textAlign));

  dispatch(
    editBtnPreFontSize({
      multi_button: values?.mainBtnSize / 16,
      multi_left_button: values?.leftBtnSize / 16,
      multi_right_button: values?.rightBtnSize / 16
    })
  );
  dispatch(
    editBtnFontSize({
      multi_button: values?.mainBtnSize / 16,
      multi_left_button: values?.leftBtnSize / 16,
      multi_right_button: values?.rightBtnSize / 16
    })
  );
  dispatch(
    editBtnFontWeight({
      multi_button: values?.mainBtnWeight,
      multi_left_button: values?.leftBtnWeight,
      multi_right_button: values?.rightBtnWeight
    })
  );
  dispatch(
    editBtnColor({
      multi_button: values?.mainBtnTextColor,
      multi_left_button: values?.leftBtnTextColor,
      multi_right_button: values?.rightBtnTextColor
    })
  );
  dispatch(
    editBtnBgColor(
      {
        multi_button: values?.mainBtnBgColorR,
        multi_left_button: values?.mainBtnBgColorR,
        multi_right_button: values?.mainBtnBgColorR
      },
      {
        multi_button: values?.mainBtnBgColorG,
        multi_left_button: values?.mainBtnBgColorG,
        multi_right_button: values?.mainBtnBgColorG
      },
      {
        multi_button: values?.mainBtnBgColorB,
        multi_left_button: values?.mainBtnBgColorB,
        multi_right_button: values?.mainBtnBgColorB
      },
      {
        multi_button: values?.mainBtnBgColorA,
        multi_left_button: 0,
        multi_right_button: 0
      }
    )
  );
  // dispatch(editBtnFontFamily(values?.btnFontFamily));
  // dispatch(editBtnTextAlign(values?.btnTextAlign));
};

export default BottomSheetSlide;
